import { Directive, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import {
  AgAnnualSignActionsComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-actions/ag-annual-sign-actions.component";
import {
  AgAnnualSignEditEmailComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-edit-email/ag-annual-sign-edit-email.component";
import {
  AgAnnualSignEditPhoneComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-edit-phone/ag-annual-sign-edit-phone.component";
import {
  AgAnnualSignNameCategoryComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-sign-name-category/ag-annual-sign-name-category.component";
import {
  AgSignatoryStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-signatory-status/ag-signatory-status.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import { PhonePipe } from "@shared/pipes/phonePipe";
import { SettingsApiService } from "@shared/services/settings-api.service";
import { Guid } from "@shared/utils/guid.helper";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { ColDef, GridApi, GridOptions, RowSelectionOptions, ValueGetterParams } from "ag-grid-community";
import { catchError, of, switchMap, tap } from "rxjs";
import { SendOption, SignGroup } from "@models/annual-statements/annual-statement-enums";
import { ApproverStatus } from "@models/documents/approver-status.enum";
import { ReminderSendingType } from "@models/reminders/reminder.enum";
import { CarbonCopy, Signatory, SignRow, UpdateRecipientInfoModel } from "@models/documents/sign-documents";
import { generateDocumentsSendByOptions } from "@shared/components/documents-related/send-by-radio/send-by-radio.component";
import { AddApproverModalComponent } from "@shared/components/documents-related/add-approver/add-approver-modal.component";
import { AddCarbonCopyComponent } from "@shared/components/documents-related/add-carbon-copy/add-carbon-copy.component";
import { ConfirmModalComponent } from "@shared/components/commons/confirm/confirm-modal.component";
import {
  ChangeSignatoryInfoModalComponent
} from "@shared/components/documents-related/change-signatory-info-modal/change-signatory-info-modal.component";
import { SignDocumentsBaseService } from "./sign-documents.base.service";

@Directive()
export abstract class SignDocumentsGridService extends SignDocumentsBaseService implements OnInit {
  settingsService = inject(SettingsApiService);

  protected approver: Signatory | undefined;
  protected carbonCopy: Signatory[] = [];
  protected signatories: Signatory[] = [];
  protected sendByOptions = generateDocumentsSendByOptions;
  protected isPhoneNumberRequired = false;

  gridApi!: GridApi;
  colDefs: ColDef[] = [];
  rows: SignRow[] = [];
  gridOptions: GridOptions = { headerHeight: 0 };
  asicFormSigningSettingReminderSendingType: ReminderSendingType | null = null;

  form = new FormGroup({
    sendBy: new FormControl<SendOption | null>(SendOption.Sign),
    email: new FormControl<string | null>(null, [Validators.required]),
    subject: new FormControl<string | null>('', [Validators.required]),
    body: new FormControl<string | null>('', [Validators.required]),
    emailForm: new FormGroup({
      subjectTemplate: new FormControl<string | null>('', [Validators.required, CustomFormValidators.minLength(1), CustomFormValidators.maxLength(100)]),
      bodyTemplate: new FormControl<string | null>('', [Validators.required, CustomFormValidators.minLength(1), CustomFormValidators.maxLength(5000)]),
      footerTemplate: new FormControl<string | null>('', [CustomFormValidators.maxLength(1000)]),
    })
  });

  override ngOnInit(): void {
    super.ngOnInit();
  }

  public onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }


  edit(data: { value: string, index: number; column: string }): void {
    const currentIndex = this.rows.some(r => r.type === SignGroup.Approver) ? data.index - 1 : data.index;
    const ccIndex = currentIndex - this.signatories.length;

    if (this.rows[data.index].type === SignGroup.Signatories) {
      if (data.column === 'email') {
        this.signatories[currentIndex].email = data.value;
      } else if (data.column === 'phone') {
        this.signatories[currentIndex].phone = data.value;
      } else if (data.column === 'fullName') {
        this.signatories[currentIndex].fullName = data.value;
      }
    }

    if (this.rows[data.index].type === SignGroup.Approver && this.approver) {
      if (data.column === 'email') {
        this.approver.email = data.value;
      } else if (data.column === 'phone') {
        this.approver.phone = data.value;
      }
    }

    if (this.rows[data.index].type === SignGroup.CarbonCopy && this.carbonCopy.length) {
      if (data.column === 'email') {
        this.carbonCopy[ccIndex].email = data.value;
      }
    }

    this.checkRowsValid();
  }

  checkRowsValid(): void {
    const invalidApproverEmail = !!this.approver && !this.approver.email;
    const invalidSignatoryName = !this.signatories?.every((signatory) => signatory.fullName);
    const invalidSignatoryEmail = !this.signatories?.every((signatory) => signatory.email?.trim());
    const invalidSignatoryPhone = this.signatories?.some((signatory) => !this.isPhoneValid(signatory.phone));
    const invalidCarbonCopyEmail = !this.carbonCopy?.every((carbonCopyItem) => carbonCopyItem.email);

    this.disableHeaderBtn(invalidApproverEmail || invalidSignatoryName || invalidSignatoryEmail || invalidSignatoryPhone || invalidCarbonCopyEmail);
  }

  public resend(index: number, sendEmail = true): void {
    if (this.documentSigning?.envelopeId == null) {
      this.toastr.error('Envelope is not created yet', 'Error');
      return;
    }

    const modalRef = this.modalFormsService.openModal(ChangeSignatoryInfoModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as ChangeSignatoryInfoModalComponent;

    instance.email = this.rows[index].email;
    instance.phone = this.rows[index].phone;
    instance.id = this.rows[index].signatureId!;
    instance.fullName = this.rows[index].fullName;
    instance.envelopeId = this.documentSigning.envelopeId;
    instance.documentId = this.documentBase.documentId;
    instance.resendEmail = sendEmail;

    instance.confirm
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((info: UpdateRecipientInfoModel) => this.updateRow(index, info.email, info.phone));
  }

  public delete(index: number): void {
    const type = this.rows[index].type;
    const rowToDelete = this.rows[index];
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = `Delete`;
    instance.message = `Are you sure you want to delete ${ rowToDelete.email } ?`;
    instance.confirmText = 'Ok';
    instance.confirm = () => of(true)
      .pipe(
        switchMap((res) => {
          if (type === SignGroup.Approver) {
            if (this.documentSigning?.approver?.recipientId) {
              return this.documentSigningsService.deleteApprover(this.documentSigning.documentSigningId).pipe(
                tap(() => {
                  this.approver = undefined;
                  this.rows.splice(index, 1);
                  this.rows = [...this.rows];
                  this.toastr.success('Approver has been deleted!', 'Success');
                })
              );
            } else {
              this.approver = undefined;
              this.rows.splice(index, 1);
              this.rows = [...this.rows];
              this.toastr.success('Approver has been deleted!', 'Success');
              return of(res);
            }
          } else if (type === SignGroup.CarbonCopy) {
            const cIndex = index - this.signatories.length - (this.approver ? 1 : 0);
            const rIndex = index - (this.approver ? 1 : 0);

            if (this.documentSigning?.carbonCopies?.length) {
              const updatedCarbonCopy = this.carbonCopy
                .filter((cc, index) => cIndex !== index)
                .map(cc => ({
                  email: cc.email,
                  phone: cc.phone,
                  fullName: cc.fullName,
                  emailSendId: Guid.EmptyGuid,
                })) as CarbonCopy[];

              return this.documentSigningsService.updateCarbonCopies(this.documentSigning.documentSigningId, updatedCarbonCopy).pipe(
                tap(() => {
                  this.carbonCopy.splice(cIndex, 1);
                  this.rows.splice(rIndex, 1);
                  this.rows = [...this.rows];
                  this.toastr.success('Carbon copy has been deleted!', 'Success');
                })
              );
            } else {
              this.carbonCopy.splice(cIndex, 1);
              this.rows.splice(rIndex, 1);
              this.rows = [...this.rows];
              this.toastr.success('Carbon copy has been deleted!', 'Success');
              return of(res);
            }
          }

          return of(res);
        }),
        catchError(err => {
          this.toastr.error('Delete error', 'Error');
          console.error(err);
          return of(null);
        }),
      );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public setGridRows(): void {
    const rows: SignRow[] = [];
    const approverAdded = !!this.approver?.email;
    const approverApproved = !!this.approver?.email && this.approver.status === ApproverStatus.Approved;

    if (this.approver) {
      rows.push({
        ...this.approver,
        groupName: 'Approver',
        type: SignGroup.Approver,
        signedOn: '',
        approverAdded,
        approverApproved
      });
    }

    if (this.signatories.length) {
      this.signatories.forEach(item => {
        rows.push({
          ...item,
          groupName: 'Signatory',
          type: SignGroup.Signatories,
          signedOn: item.signedOn,
          approverAdded,
          approverApproved
        });
      });
    }

    if (this.carbonCopy.length) {
      this.carbonCopy.forEach(item => {
        rows.push({
          ...item,
          groupName: 'Carbon Copy',
          type: SignGroup.CarbonCopy,
          signedOn: '',
          approverAdded,
          approverApproved
        });
      });
    }

    this.rows = [...rows];
    this.checkRowsValid();
  }

  public updateRow(index: number, email: string, phone: string): void {
    const signatoryId = this.rows[index].signatureId;

    if (this.rows[index].type === SignGroup.Signatories) {
      const signatory = this.signatories.find(s => s.signatureId === signatoryId);
      if (signatory) {
        signatory.email = email;
        signatory.phone = phone;
      }
    }

    if (this.rows[index].type === SignGroup.Approver) {
      this.approver!.email = email;
      this.approver!.phone = phone;
    }

    this.setGridRows();
  }

  public setApprover(): void {
    if (this.documentSigning?.approver) {
      const { email, recipientId, phone, fullName, approverStatus, approvedOn } = this.documentSigning.approver;
      this.approver = {
        id: '',
        email: email ?? '',
        phone: phone ?? '',
        fullName: fullName ?? '',
        status: approverStatus ?? 0,
        approvedOn: approvedOn ?? '',
        signatureId: recipientId,
        disabled: false
      };
    }
  }

  public addApprover(): void {
    const modalRef = this.modalFormsService.openModal(AddApproverModalComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as AddApproverModalComponent;

    componentInstance.confirm.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((approver: Signatory) => {
      this.approver = approver;
      this.setGridRows();
    });
  }

  public addCarbonCopy(): void {
    const modalRef = this.modalFormsService.openModal(AddCarbonCopyComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as AddCarbonCopyComponent;

    componentInstance.confirm
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((email: string) => {
        this.carbonCopy.push({
          id: '',
          fullName: '',
          email: email,
          phone: '',
          signatureId: '',
          disabled: false
        });
        this.setGridRows();
      });
  }

  disableRow(value: boolean): void {
    this.setGridRows();
    this.rows = this.rows.map((row) => {
      row.disabled = value;
      return row;
    });
  }

  isPhoneValid(phone: string, isPhoneNumberRequired = this.isPhoneNumberRequired): boolean {
    const control = { value: phone } as AbstractControl;
    const errors = CustomFormValidators.phoneNumberValidator(control) ?? CustomFormValidators.minLength(6)(control);

    if (!phone?.trim() && isPhoneNumberRequired) return false;

    return errors === null;
  }

  // Grid Configs

  readonly signListRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  public setGridConfig(): void {
    this.colDefs = [
      {
        headerName: 'Name',
        field: 'fullName',
        flex: 1,
        cellRenderer: AgAnnualSignNameCategoryComponent,
        menuTabs: [],
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 0.5,
        cellClass: 'custom-editable-cell',
        cellRenderer: AgAnnualSignEditEmailComponent,
        cellRendererParams: {
          edit: this.edit.bind(this),
        },
        menuTabs: [],
      },
      {
        headerName: 'Phone',
        field: 'phone',
        flex: 0.45,
        hide: false,
        cellClass: 'custom-editable-cell',
        cellRenderer: AgAnnualSignEditPhoneComponent,
        cellRendererParams: {
          edit: this.edit.bind(this),
          isRequired: this.isPhoneNumberRequired,
        },
        menuTabs: [],
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 180,
        flex: 1,
        hide: true,
        editable: false,
        cellRenderer: AgSignatoryStatusComponent,
        menuTabs: [],
      },
      {
        headerName: '',
        field: '',
        flex: 0.08,
        hide: false,
        sortable: false,
        cellClass: 'action-cell',
        cellRenderer: AgAnnualSignActionsComponent,
        cellRendererParams: {
          delete: this.delete.bind(this),
          resend: this.resend.bind(this),
        },
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }

  public setEnvelopeGridConfig(): void {
    this.colDefs = [
      {
        headerName: 'Name',
        field: 'fullName',
        flex: 1.5,
        cellRenderer: AgAnnualSignNameCategoryComponent,
        menuTabs: [],
      },
      {
        headerName: 'Status',
        field: 'status',
        flex: 1.5,
        hide: false,
        cellRenderer: AgSignatoryStatusComponent,
        menuTabs: [],
      },
      {
        headerName: 'Email',
        field: 'email',
        hide: false,
        flex: 1.5,
        menuTabs: [],
      },
      {
        headerName: 'Phone',
        field: 'phone',
        flex: 1,
        valueGetter: (params: ValueGetterParams<SignRow, SignRow>): string => {
          return new PhonePipe().transform(params.data?.phone);
        },
        hide: false,
        menuTabs: [],
      },
      {
        headerName: '',
        field: '',
        width: 60,
        hide: false,
        sortable: false,
        cellRenderer: AgAnnualSignActionsComponent,
        cellRendererParams: {
          delete: this.delete.bind(this),
          resend: this.resend.bind(this),
          editInfo: this.resend.bind(this)
        },
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }
}
